import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Heart,
  CheckCircle,
  ArrowRight,
  Quote,
  Target,
  Users,
  Brain,
  RefreshCw,
  XCircle,
  User,
  ChevronRight,
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useTranslation } from "react-i18next";

// --------------------
// UI SUB-COMPONENTS
// --------------------

const Bullet = ({ children, icon: Icon = CheckCircle, color = "text-warm-sage" }) => (
  <li className="flex items-start gap-3 mb-3">
    <Icon className={`h-5 w-5 ${color} mt-0.5 flex-shrink-0`} />
    <span className="text-charcoal/90 leading-relaxed text-sm md:text-base">{children}</span>
  </li>
);

// Interactive, lightweight quiz (kept; video removed)
const SelfDiscoveryQuizV4 = ({ search }) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const questions = [
    {
      question: t("art.quiz.questions.0.question"),
      options: [
        t("art.quiz.questions.0.options.0"),
        t("art.quiz.questions.0.options.1"),
        t("art.quiz.questions.0.options.2"),
        t("art.quiz.questions.0.options.3"),
      ],
    },
    {
      question: t("art.quiz.questions.1.question"),
      options: [
        t("art.quiz.questions.1.options.0"),
        t("art.quiz.questions.1.options.1"),
        t("art.quiz.questions.1.options.2"),
        t("art.quiz.questions.1.options.3"),
      ],
    },
    {
      question: t("art.quiz.questions.2.question"),
      options: [
        t("art.quiz.questions.2.options.0"),
        t("art.quiz.questions.2.options.1"),
        t("art.quiz.questions.2.options.2"),
        t("art.quiz.questions.2.options.3"),
      ],
    },
  ];

  const handleAnswer = (answerIndex) => {
    setIsTransitioning(true);
    const next = { ...answers, [currentQuestion]: answerIndex };
    setAnswers(next);
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion((q) => q + 1);
        setIsTransitioning(false);
      } else {
        setShowResult(true);
      }
    }, 280);
  };

  const handleReset = () => {
    setAnswers({});
    setCurrentQuestion(0);
    setShowResult(false);
    setIsTransitioning(false);
  };

  const calculateResult = () => {
    const score = Object.values(answers).reduce((sum, a) => sum + a, 0);
    if (score <= 3) return "aligne";
    if (score <= 6) return "en-conscience";
    return "en-transformation";
  };

  if (showResult) {
    const result = calculateResult();
    const results = {
      aligne: {
        title: t("art.quiz.results.aligne.title"),
        message: t("art.quiz.results.aligne.message"),
      },
      "en-conscience": {
        title: t("art.quiz.results.enConscience.title"),
        message: t("art.quiz.results.enConscience.message"),
      },
      "en-transformation": {
        title: t("art.quiz.results.enTransformation.title"),
        message: t("art.quiz.results.enTransformation.message"),
      },
    };

    return (
      <Card className="p-6 md:p-8 bg-gradient-to-br from-warm-sage/20 to-soft-taupe/10 border-warm-sage/30 shadow-xl text-center">
        <Target className="h-12 w-12 md:h-16 md:w-16 text-warm-sage mx-auto mb-4 md:mb-5" />
        <h3 className="text-xl md:text-2xl font-serif text-charcoal mb-3">
          {results[result].title}
        </h3>
        <p className="text-sm md:text-lg text-charcoal/80 mb-6 leading-relaxed">
          {results[result].message}
        </p>
        <a href={`/${search}#contact`}>
          <Button
            size="lg"
            className="bg-warm-sage hover:bg-warm-sage/90 text-cream-white w-full rounded-full text-sm md:text-base"
          >
            {t("art.quiz.buttonPrimary")}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </a>
        <Button
          variant="ghost"
          onClick={handleReset}
          className="text-warm-gray-600 mt-4 text-xs md:text-sm"
        >
          <RefreshCw className="mr-2 h-3 w-3" />
          {t("art.quiz.buttonRetry")}
        </Button>
      </Card>
    );
  }

  return (
    <Card className="p-6 md:p-8 bg-white/95 backdrop-blur-sm border-warm-sage/20 shadow-xl overflow-hidden">
      <div className="mb-5 md:mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-[11px] md:text-xs text-warm-gray-600 tracking-wider uppercase">
            {t("art.quiz.label")}
          </span>
          <span className="text-[11px] md:text-xs text-warm-gray-600">
            {currentQuestion + 1} / {questions.length}
          </span>
        </div>
        <div className="w-full bg-soft-gray/30 rounded-full h-1.5">
          <div
            className="bg-warm-sage h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      <div
        className={`transition-opacity duration-300 ${
          isTransitioning ? "opacity-0" : "opacity-100"
        }`}
      >
        <p className="font-serif text-charcoal text-center text-xl md:text-2xl leading-relaxed mb-6 md:mb-8 min-h-[90px]">
          {questions[currentQuestion].question}
        </p>
        <div className="space-y-2.5 md:space-y-3">
          {questions[currentQuestion].options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswer(index)}
              className="w-full p-3 md:p-4 flex justify-between items-center rounded-lg border border-soft-gray/30 hover:border-warm-sage hover:bg-warm-sage/10 transition-all duration-200 text-charcoal/80 text-sm md:text-base text-left group"
            >
              <span>{option}</span>
              <ChevronRight className="h-4 w-4 md:h-5 md:w-5 text-warm-sage opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>
      </div>
    </Card>
  );
};

// --------------------
// MAIN PAGE
// --------------------

export default function ArtEtreSoiElite() {
  const { search } = useLocation();
  const [activeTab, setActiveTab] = useState("avant");
  const { t } = useTranslation();

  return (
    <section className="bg-cream-white">
      {/* HERO */}
      <div className="relative h-[55vh] md:h-[60vh] min-h-[360px] w-full flex items-end pb-10 md:pb-35">
        <img
          src="https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=2000&q=80"
          alt={t("art.hero.alt")}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-charcoal/40 to-transparent z-10" />
        <div className="absolute top-0 inset-x-0 h-20 bg-gradient-to-b from-charcoal/40 to-transparent z-10" />

        <div className="relative max-w-6xl mx-auto px-6 w-full z-20 pt-10 md:pt-14">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-cream-white/20 backdrop-blur-sm border border-cream-white/30">
              <Heart className="h-3 w-3 md:h-4 md:w-4 text-cream-white" />
              <span className="text-cream-white/90 text-[10px] md:text-xs tracking-[0.25em] uppercase">
                {t("art.hero.badge")}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-light text-cream-white mb-3 md:mb-4 leading-tight">
              {t("art.hero.title")}
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-cream-white/90 mb-5 max-w-2xl font-light">
              {t("art.hero.subtitle")}
            </p>

            {/* Subnav chips */}
            <div className="flex flex-wrap gap-2">
              <a
                href="#scope"
                className="text-[11px] md:text-xs px-3 py-1.5 rounded-full border border-cream-white/70 text-cream-white/95 hover:bg-cream-white/10"
              >
                {t("art.hero.links.scope")}
              </a>
              <a
                href="#risques"
                className="text-[11px] md:text-xs px-3 py-1.5 rounded-full border border-cream-white/70 text-cream-white/95 hover:bg-cream-white/10"
              >
                {t("art.hero.links.prevention")}
              </a>
              <a
                href={`/${search}#contact`}
                className="text-[11px] md:text-xs px-3 py-1.5 rounded-full border border-cream-white/70 text-cream-white/95 hover:bg-cream-white/10"
              >
                {t("art.hero.links.contact")}
              </a>
            </div>

            <div className="mt-4">
              <a href={`/${search}#contact`}>
                <Button className="bg-warm-sage hover:bg-warm-sage/90 text-cream-white px-6 md:px-8 py-2.5 md:py-3 text-sm md:text-base rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5">
                  {t("art.hero.cta")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="max-w-6xl mx-auto px-6 py-14 md:py-20">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* LEFT COLUMN */}
          <div className="lg:col-span-2 space-y-8">
            {/* INTRO */}
            <Card className="p-6 md:p-8 bg-white/95 backdrop-blur-sm border-soft-gray/20 shadow-xl">
              <div className="flex items-start gap-4 md:gap-5">
                <Users className="h-8 w-8 md:h-10 md:w-10 text-warm-sage flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl md:text-3xl font-serif text-charcoal mb-3 md:mb-4">
                    {t("art.intro.title")}
                  </h2>
                  <p className="text-sm md:text-lg text-charcoal/80 leading-relaxed mb-4 md:mb-6">
                    {t("art.intro.p1.beforeQuote")}
                    <span className="italic font-medium">
                      {t("art.intro.p1.quote")}
                    </span>
                  </p>
                  <div className="grid md:grid-cols-2 gap-5 mt-4 md:mt-6">
                    <div className="p-5 rounded-lg bg-soft-taupe/10 border border-soft-taupe/20">
                      <h4 className="font-semibold text-charcoal mb-3 flex items-center gap-3 text-sm md:text-base">
                        <Users className="h-4 w-4 md:h-5 md:w-5 text-soft-taupe" />
                        {t("art.intro.man.title")}
                      </h4>
                      <p className="text-sm text-charcoal/70 leading-relaxed">
                        {t("art.intro.man.body")}
                      </p>
                    </div>
                    <div className="p-5 rounded-lg bg-warm-sage/10 border border-warm-sage/20">
                      <h4 className="font-semibold text-charcoal mb-3 flex items-center gap-3 text-sm md:text-base">
                        <User className="h-4 w-4 md:h-5 md:w-5 text-warm-sage" />
                        {t("art.intro.woman.title")}
                      </h4>
                      <p className="text-sm text-charcoal/70 leading-relaxed">
                        {t("art.intro.woman.body")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* SCOPE BLOCK */}
            <Card id="scope" className="p-6 md:p-7 bg-white/95 border-soft-gray/30 shadow-lg">
              <h3 className="text-lg md:text-xl font-semibold text-charcoal mb-3">
                {t("art.scope.title")}
              </h3>
              <p className="text-sm md:text-base text-charcoal/85 leading-relaxed mb-4">
                {t("art.scope.description")}
              </p>
              <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
                <div className="rounded-lg border border-soft-gray/30 p-3">
                  <p className="text-sm text-charcoal/90">
                    {t("art.scope.cards.0")}
                  </p>
                </div>
                <div className="rounded-lg border border-soft-gray/30 p-3">
                  <p className="text-sm text-charcoal/90">
                    {t("art.scope.cards.1")}
                  </p>
                </div>
                <div className="rounded-lg border border-soft-gray/30 p-3">
                  <p className="text-sm text-charcoal/90">
                    {t("art.scope.cards.2")}
                  </p>
                </div>
                <div className="rounded-lg border border-soft-gray/30 p-3">
                  <p className="text-sm text-charcoal/90">
                    {t("art.scope.cards.3")}
                  </p>
                </div>
              </div>
              <p className="text-[11px] md:text-xs text-warm-gray mt-4 leading-relaxed">
                {t("art.scope.notice")}
              </p>
            </Card>

            {/* PREVENTION BLOCK */}
            <Card
              id="risques"
              className="p-6 md:p-7 bg-gradient-to-br from-rose-50 via-cream-white to-soft-taupe/20 border border-rose-100 shadow-md relative overflow-hidden"
            >
              <div className="pointer-events-none absolute -right-10 -top-10 w-40 h-40 rounded-full bg-rose-200/40 blur-3xl opacity-70" />
              <div className="relative flex flex-col md:flex-row gap-6 md:gap-8 items-start">
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-rose-100/70 border border-rose-200">
                    <XCircle className="h-4 w-4 text-rose-600" />
                    <span className="text-[11px] md:text-xs tracking-[0.18em] uppercase text-rose-700">
                      {t("art.prevention.badge")}
                    </span>
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-charcoal mb-3">
                    {t("art.prevention.title")}
                  </h3>
                  <p className="text-sm md:text-base text-charcoal/85 leading-relaxed mb-3">
                    {t("art.prevention.p1")}
                  </p>
                  <p className="text-xs md:text-sm text-charcoal/80 leading-relaxed mb-3">
                    {t("art.prevention.p2")}
                  </p>
                  <p className="text-[11px] md:text-xs text-warm-gray leading-relaxed">
                    {t("art.prevention.p3")}
                  </p>
                </div>
                <div className="w-full md:w-64 lg:w-72">
                  <div className="rounded-xl bg-white/80 border border-soft-gray/40 p-4 md:p-5 shadow-sm">
                    <p className="text-xs md:text-sm font-medium text-charcoal mb-3 flex items-center gap-2">
                      <Brain className="h-4 w-4 text-warm-sage" />
                      {t("art.prevention.sidebar.title")}
                    </p>
                    <ul className="space-y-2.5 text-xs md:text-sm text-charcoal/85">
                      <li className="flex gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-rose-500 flex-shrink-0" />
                        <span>{t("art.prevention.sidebar.items.0")}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                        <span>{t("art.prevention.sidebar.items.1")}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-warm-sage flex-shrink-0" />
                        <span>{t("art.prevention.sidebar.items.2")}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-soft-taupe flex-shrink-0" />
                        <span>{t("art.prevention.sidebar.items.3")}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </Card>

            {/* AVANT / APRES */}
            <Card className="p-6 md:p-8 bg-gradient-to-br from-white to-soft-taupe/5 border-warm-sage/20 shadow-lg">
              <div className="flex border-b border-soft-gray/20 mb-5 md:mb-6">
                <button
                  onClick={() => setActiveTab("avant")}
                  className={`flex-1 py-2.5 md:py-3 text-sm md:text-base font-medium transition-colors duration-300 ${
                    activeTab === "avant"
                      ? "text-warm-sage border-b-2 border-warm-sage"
                      : "text-charcoal/60 hover:text-charcoal"
                  }`}
                >
                  {t("art.tabs.before")}
                </button>
                <button
                  onClick={() => setActiveTab("apres")}
                  className={`flex-1 py-2.5 md:py-3 text-sm md:text-base font-medium transition-colors duration-300 ${
                    activeTab === "apres"
                      ? "text-warm-sage border-b-2 border-warm-sage"
                      : "text-charcoal/60 hover:text-charcoal"
                  }`}
                >
                  {t("art.tabs.after")}
                </button>
              </div>
              {activeTab === "avant" && (
                <div className="space-y-3 md:space-y-4">
                  <Bullet icon={XCircle} color="text-rose-500">
                    {t("art.tabs.beforeItems.0")}
                  </Bullet>
                  <Bullet icon={XCircle} color="text-rose-500">
                    {t("art.tabs.beforeItems.1")}
                  </Bullet>
                  <Bullet icon={XCircle} color="text-rose-500">
                    {t("art.tabs.beforeItems.2")}
                  </Bullet>
                </div>
              )}
              {activeTab === "apres" && (
                <div className="space-y-3 md:space-y-4">
                  <Bullet icon={CheckCircle} color="text-green-600">
                    {t("art.tabs.afterItems.0")}
                  </Bullet>
                  <Bullet icon={CheckCircle} color="text-green-600">
                    {t("art.tabs.afterItems.1")}
                  </Bullet>
                  <Bullet icon={CheckCircle} color="text-green-600">
                    {t("art.tabs.afterItems.2")}
                  </Bullet>
                </div>
              )}
            </Card>

            {/* CTA */}
            <Card className="p-6 md:p-8 bg-white border-soft-gray/20 shadow-lg text-center">
              <h3 className="text-xl md:text-2xl font-serif text-charcoal mb-4">
                {t("art.cta.title")}
              </h3>
              <p className="text-sm md:text-base text-charcoal/80 mb-6">
                {t("art.cta.body")}
              </p>
              <a href={`/${search}#contact`}>
                <Button className="bg-warm-sage hover:bg-warm-sage/90 text-cream-white px-8 md:px-10 py-2.5 md:py-3 text-sm md:text-base rounded-full shadow-lg hover:shadow-xl transition-all">
                  {t("art.cta.button")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </Card>
          </div>

         {/* RIGHT COLUMN */}
        <div className="space-y-8 lg:sticky lg:top-24">
            <SelfDiscoveryQuizV4 search={search} />

            <Card className="p-6 bg-white/80 backdrop-blur-sm border-soft-taupe/30 shadow-lg">
              <div className="text-center mb-4">
                <Quote className="h-6 w-6 md:h-8 md:w-8 text-warm-sage mx-auto" />
              </div>
              <p className="text-sm md:text-base text-charcoal/80 italic text-center mb-4 md:mb-5 leading-relaxed">
                {t("art.testimonial.quote")}
              </p>
          <div className="flex items-center justify-center gap-3">
              {/*  <img
                  src="https://images.pexels.com/photos/614810/pexels-photo-614810.jpeg?auto=format&fit=crop&w=200&q=60"
                  alt={t("art.testimonial.alt")}
                  className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-gray-200 object-cover border-2 border-warm-sage/40"
                />*/}
                <div>
                  <p className="text-xs md:text-sm font-medium text-charcoal">
                    {t("art.testimonial.name")}
                  </p>
                  <p className="text-[11px] text-warm-gray">
                    {t("art.testimonial.meta")}
                  </p>  
                </div>  
              </div>  
            </Card>
          </div>
        </div> 
      </div>
    </section>
  );
}
